import React, { useState, useEffect } from 'react';
import TechnicianPortal from './portals/TechnicianPortal';
import AdminPortal from './portals/AdminPortal';

type Page = 'login' | 'signup' | 'forgot' | 'reset';
type Plan = 'standard';

const PLAN_DETAILS: Record<Plan, { label: string; price: string }> = {
  standard: { label: 'Standard', price: '£6/month' },
};

const container = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #0f172a 0%, #1e3c72 50%, #2a5298 100%)',
  position: 'relative' as const,
  overflow: 'hidden'
};

const card = {
  background: 'rgba(255,255,255,0.97)',
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  padding: '40px 36px',
  width: '420px',
  maxWidth: '90vw',
  boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
  position: 'relative' as const,
  zIndex: 1
};

const inputGroup = { marginBottom: '16px' };
const label = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' };
const input = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  fontSize: '14px',
  outline: 'none',
  transition: 'border-color 0.2s',
  boxSizing: 'border-box' as const
};

function App() {
  const [page, setPage] = useState<Page>('login');

  // Auth state
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [userRole, setUserRole] = useState<string | null>(null);

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Signup state
  const [signupEmail, setSignupEmail] = useState('');
  const [signupFirstName, setSignupFirstName] = useState('');
  const [signupLastName, setSignupLastName] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirm, setSignupConfirm] = useState('');
  const [signupPlan, setSignupPlan] = useState<Plan>('standard');
  const [signupError, setSignupError] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Reset password state
  const [resetToken, setResetToken] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [tokenVerified, setTokenVerified] = useState(false);

  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role);
      } catch (e) { console.error(e); }
    }
  }, [token]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await res.json();
      if (data.token) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setUserRole(data.user.role);
      } else {
        setLoginError(data.error || 'Login failed');
      }
    } catch (e) { setLoginError('Connection error'); }
    finally { setLoginLoading(false); }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupError('');
    if (signupPassword !== signupConfirm) {
      setSignupError('Passwords do not match');
      return;
    }
    setSignupLoading(true);
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: signupEmail, password: signupPassword, first_name: signupFirstName, last_name: signupLastName, plan_type: signupPlan }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Signup failed');
      }
      const data = await res.json();
      if (data.token) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setUserRole(data.user.role);
      } else {
        setSignupSuccess(true);
      }
    } catch (err: any) {
      setSignupError(err.message || 'Something went wrong');
    } finally {
      setSignupLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError('');
    setForgotMsg('');
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      if (data.success) {
        setForgotSent(true);
        setForgotMsg(data.message || 'Check your email for the reset link.');
      } else {
        setForgotError(data.error || 'Something went wrong');
      }
    } catch (e) { setForgotError('Connection error'); }
    finally { setForgotLoading(false); }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetMsg('');
    if (resetPassword !== resetConfirm) {
      setResetError('Passwords do not match');
      setResetLoading(false);
      return;
    }
    if (resetPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      setResetLoading(false);
      return;
    }
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, newPassword: resetPassword })
      });
      const data = await res.json();
      if (data.success) {
        setResetDone(true);
        setResetMsg('Password has been reset successfully. You can now sign in.');
      } else {
        setResetError(data.error || 'Failed to reset password');
      }
    } catch (e) { setResetError('Connection error'); }
    finally { setResetLoading(false); }
  };

  // Check URL for reset token on mount
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      setResetToken(token);
      setPage('reset');
      // Verify token
      fetch('/api/auth/verify-reset-token?token=' + encodeURIComponent(token))
        .then(r => r.json())
        .then(d => {
          if (d.valid) { setResetEmail(d.email); setTokenVerified(true); }
          else { setResetError(d.error || 'Invalid or expired link'); }
        })
        .catch(() => setResetError('Could not verify reset link'));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUserRole(null);
    window.location.reload();
  };

  // If authenticated, show portal
  if (token && userRole) {
    if (userRole === 'admin') {
      return <AdminPortal token={token} onLogout={handleLogout} />;
    }
    return <TechnicianPortal token={token} onLogout={handleLogout} />;
  }

  // Auth pages (login / signup)
  return (
    <div style={container}>
      <div style={{
        position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%',
        background: 'radial-gradient(circle at 30% 50%, rgba(59,130,246,0.08) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(16,185,129,0.06) 0%, transparent 50%)',
        pointerEvents: 'none'
      }} />
      <div style={card}>
        {/* Brand icon + title (iOS-style) */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '100px', height: '100px', background: 'linear-gradient(135deg, #1e3c72, #2a5298)',
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', boxShadow: '0 8px 24px rgba(30,60,114,0.3)'
          }}><svg width='48' height='48' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' style={{display:'block',margin:'0 auto'}}><path d='M12 2L3 7v5c0 5.5 3.8 10.7 9 12 5.2-1.3 9-6.5 9-12V7l-9-5z' fill='white'/><rect x='9' y='11' width='6' height='5' rx='0.8' fill='#1e3c72'/><path d='M9.5 11V9A2.5 2.5 0 0 1 14.5 9v2' stroke='#1e3c72' strokeWidth='1.5' fill='none' strokeLinecap='round'/></svg></div>
          <h1 style={{ fontSize: '34px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0', lineHeight: 1.2 }}>LockTrak</h1>
          <p style={{ fontSize: '15px', color: '#94a3b8', margin: 0 }}>Vehicle Security Platform</p>
        </div>

        {page === 'login' ? (
          /* Login form */
          <form onSubmit={handleLogin} action="#" method="post">
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px', textAlign: 'center' }}>Sign In</h1>
            <p style={{ fontSize: '14px', color: '#94a3b8', margin: '0 0 24px', textAlign: 'center' }}>Welcome back to LockTrak</p>

            {loginError && (
              <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', fontSize: '13px', marginBottom: '16px', textAlign: 'center' }}>
                {loginError}
              </div>
            )}

            <div style={inputGroup}>
              <label style={label}>Email</label>
              <input type="email" placeholder="you@example.com" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} style={input}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} required />
            </div>
            <div style={inputGroup}>
              <label style={label}>Password</label>
              <input type="password" placeholder="Your password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} style={input}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} required />
            </div>
            <div style={{ textAlign: 'right', marginBottom: '16px' }}>
              <button type="button" onClick={() => { setPage('forgot'); setLoginError(''); setForgotEmail(''); setForgotMsg(''); setForgotError(''); setForgotSent(false); }}
                style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                Forgot password?
              </button>
            </div>
            <button type="button" onClick={handleLogin} disabled={loginLoading}
              style={{
                width: '100%', padding: '12px', background: loginLoading ? '#94a3b8' : 'linear-gradient(135deg, #1e3c72, #2a5298)',
                color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600',
                cursor: loginLoading ? 'wait' : 'pointer', transition: 'all 0.2s',
                boxShadow: loginLoading ? 'none' : '0 4px 12px rgba(30,60,114,0.3)'
              }}
              onMouseEnter={e => { if (!loginLoading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { if (!loginLoading) e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {loginLoading ? 'Signing in...' : 'Sign In'}
            </button>
            <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#94a3b8' }}>
              Don't have an account?{' '}
              <button type="button" onClick={() => { setPage('signup'); setLoginError(''); }}
                style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                Create account
              </button>
            </p>
          </form>
        ) : (
          /* Signup form */
          <form onSubmit={handleSignup} action="#" method="post">
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px', textAlign: 'center' }}>Create Account</h1>
            <p style={{ fontSize: '14px', color: '#94a3b8', margin: '0 0 24px', textAlign: 'center' }}>Start your LockTrak journey</p>

            {signupError && (
              <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', fontSize: '13px', marginBottom: '16px', textAlign: 'center' }}>
                {signupError}
              </div>
            )}

            {signupSuccess ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p style={{ color: '#065f46', fontSize: '14px', marginBottom: '16px' }}>Account created successfully! You can now sign in.</p>
                <button type="button" onClick={() => { setPage('login'); setSignupSuccess(false); }}
                  style={{ padding: '10px 22px', background: 'linear-gradient(135deg, #1e3c72, #2a5298)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                  Sign In
                </button>
              </div>
            ) : (
              <>
                <div style={inputGroup}>
                  <label style={label}>Email</label>
                  <input type="email" placeholder="you@example.com" value={signupEmail} onChange={e => setSignupEmail(e.target.value)} style={input}
                    onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} required />
                </div>
                <div style={{ display: 'flex', gap: '12px', ...inputGroup }}>
                  <div style={{ flex: 1 }}>
                    <label style={label}>First Name</label>
                    <input type="text" placeholder="Jane" value={signupFirstName} onChange={e => setSignupFirstName(e.target.value)} style={input}
                      onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} required />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={label}>Last Name</label>
                    <input type="text" placeholder="Doe" value={signupLastName} onChange={e => setSignupLastName(e.target.value)} style={input}
                      onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} required />
                  </div>
                </div>
                <div style={inputGroup}>
                  <label style={label}>Password</label>
                  <input type="password" placeholder="Min. 8 characters" value={signupPassword} onChange={e => setSignupPassword(e.target.value)} style={input}
                    onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} minLength={8} required />
                </div>
                <div style={inputGroup}>
                  <label style={label}>Confirm Password</label>
                  <input type="password" placeholder="Re-enter password" value={signupConfirm} onChange={e => setSignupConfirm(e.target.value)} style={input}
                    onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} minLength={8} required />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={label}>Plan</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {(Object.entries(PLAN_DETAILS) as [Plan, { label: string; price: string }][]).map(([key, plan]) => (
                      <button key={key} type="button" onClick={() => setSignupPlan(key)}
                        style={{
                          flex: 1, padding: '12px 8px', borderRadius: '10px', cursor: 'pointer',
                          border: signupPlan === key ? '2px solid #1e3c72' : '1px solid #e2e8f0',
                          background: signupPlan === key ? '#eff6ff' : 'white',
                          textAlign: 'center', transition: 'all 0.2s'
                        }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{plan.label}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{plan.price}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <button type="button" onClick={handleSignup} disabled={signupLoading}
                  style={{
                    width: '100%', padding: '12px', background: signupLoading ? '#94a3b8' : 'linear-gradient(135deg, #1e3c72, #2a5298)',
                    color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600',
                    cursor: signupLoading ? 'wait' : 'pointer',
                    boxShadow: signupLoading ? 'none' : '0 4px 12px rgba(30,60,114,0.3)'
                  }}
                  onMouseEnter={e => { if (!signupLoading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { if (!signupLoading) e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {signupLoading ? 'Creating account...' : 'Create Account'}
                </button>
                <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#94a3b8' }}>
                  Already have an account?{' '}
                  <button type="button" onClick={() => { setPage('login'); setSignupError(''); }}
                    style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                    Sign In
                  </button>
                </p>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

export default App;
